import { memo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { CustomDialog } from '../CustomDialog';
import { ThumbnailImage } from './ThumbnailImage';
import { useVideoCardMobile } from './useVideoCardMobile';
import { VideoCardMobileProps } from './types';
import { useOriginAccess } from '../../hooks/useOriginAccess';
import { hasSpecialtyAssigned } from '../../utils/videoSpecialtyStatus';
import type { Swiper as SwiperType } from 'swiper';
import { getThumbnailUrl, getDefaultThumbnailUrl, isThumbnailPending } from '../../utils/thumbnailUtils';
import { KebabMenu } from '../common/KebabMenu';
import { normalizeVideoImagePath } from '../../utils/videoImagePath';
import { fetchExternalVideoThumbnail } from '../../services/externalVideoApi/externalVideoApi';
import { FALLBACK_LABELS } from '../../config/constants';
import { VISIBILITY_BADGE_LABELS } from '../../utils/visibilityLabels';

// SwiperCSSのインポート
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import { formatDate as utilFormatDate } from './utils';
import styles from './VideoCardMobile.module.css';

/**
 * 外部動画用モバイルカードコンポーネント
 */
const ExternalVideoCardMobile = ({ video }: VideoCardMobileProps) => {
  const navigate = useNavigate();
  const hospitalName = video.hospital_name || FALLBACK_LABELS.EXTERNAL_HOSPITAL;
  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<string | undefined>(undefined);
  const blobUrlRef = useRef<string>(undefined);

  useEffect(() => {
    if (!video.external_id) return;
    let revoked = false;

    fetchExternalVideoThumbnail(video.external_id)
      .then(blobUrl => {
        if (!revoked) {
          blobUrlRef.current = blobUrl;
          setThumbnailBlobUrl(blobUrl);
        } else {
          URL.revokeObjectURL(blobUrl);
        }
      })
      .catch((err: unknown) => {
        console.error(`サムネイル取得失敗 (external_id=${video.external_id}):`, err);
      });

    return () => {
      revoked = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = undefined;
      }
    };
  }, [video.external_id]);

  const handleClick = () => {
    if (video.external_id && video.external_status === 'synced') {
      navigate(`/external-videos/${video.external_id}`);
    }
  };

  const isPlayable = video.external_status === 'synced';

  return (
    <div
      className={`${styles['video-card-mobile-container']} ${isPlayable ? styles['video-card-mobile-container--playable'] : styles['video-card-mobile-container--disabled']}`}
      data-testid="video-card-mobile-external"
      onClick={handleClick}
    >
      {/* サムネイル */}
      <div className={styles['video-card-mobile-thumbnail-wrapper']}>
        {thumbnailBlobUrl ? (
          <ThumbnailImage
            src={thumbnailBlobUrl}
            alt={video.title || video.name || FALLBACK_LABELS.UNTITLED}
          />
        ) : (
          <div className={styles['video-card-mobile-external-thumbnail']}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" stroke="#999" strokeWidth="1.5"/>
              <polygon points="9.5,8 16.5,12 9.5,16" fill="#999"/>
            </svg>
          </div>
        )}
      </div>

      {/* 情報部分 */}
      <div className={styles['video-card-mobile-info-wrapper']}>
        {/* タイトル */}
        <h3
          className={styles['video-card-mobile-title']}
          title={video.title || video.name || FALLBACK_LABELS.UNTITLED}
        >
          {video.title || video.name || FALLBACK_LABELS.UNTITLED}
        </h3>

        {/* アイコン + メタ情報 */}
        <div className={styles['video-card-mobile-meta-row']}>
          <img
            className={styles['video-card-mobile-uploader-icon']}
            src="/asset/no_icon_user.png"
            alt="Hospital Icon"
          />
          <div className={styles['video-card-mobile-meta-text']}>
            <span className={styles['video-card-mobile-channel-name']}>
              {hospitalName}
            </span>
            <div className={styles['video-card-mobile-meta']}>
              <span className={styles['video-card-mobile-date']}>
                {`投稿日:${utilFormatDate(video.created_at)}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * モバイル向け動画カードコンポーネント
 * スマートフォンなどの小さい画面向けに最適化された動画カード
 */
const VideoCardMobile = (props: VideoCardMobileProps) => {
  if (props.video.isExternal) {
    return <ExternalVideoCardMobile {...props} />;
  }
  return <InternalVideoCardMobile {...props} />;
};

const InternalVideoCardMobile = (props: VideoCardMobileProps) => {
  const { video } = props;

  // 接続元判定: サーバー API /api/access/origin を起動時1回取得。
  // isLoading 中は isExternal=false（活性のままサーバー 403 に委ねる / UX 原則）。
  // isExternal=true の場合、ケバブメニューのDLボタンを disabled にする（UX R1-a）。
  const { isExternal } = useOriginAccess();
  // 診療科未設定の動画は公開系メニュー（院内公開にする／外部公開にする）を disabled にする。
  // 判定は3種のカードで共通のユーティリティに集約している（videoSpecialtyStatus.ts）。
  const hasSpecialty = hasSpecialtyAssigned(video);

  // 処理中動画の判定: is_processingフラグまたはIDがsession_で始まる場合
  const isVideoProcessing = video.is_processing || String(video.id).startsWith('session_');

  const {
    isFavorite,
    visibility,
    dialogOpen,
    dialogConfig,
    hasBeenSwiped,
    isAutoSwipe,
    swiperRef,
    uploaderIconUrl,
    channelLabel,
    isOwner,
    images,
    hasMultipleImages,
    isPrivate,
    handleSlideChange,
    handleTouchStart,
    handleNavigation,
    handleAutoplayStart,
    handleContainerClick,
    setDialogOpen,
    formatDuration,
    formatDate,
    // ケバブメニュー関連
    kebabMenu
  } = useVideoCardMobile(props);

  return (
    <div
      className={styles['video-card-mobile-container']}
      onClick={handleContainerClick}
      data-testid="video-card-mobile"
    >
      {/* サムネイル部分 */}
      <div className={styles['video-card-mobile-thumbnail-wrapper']}>
        {images.length > 0 ? (
          <>
            <Swiper
              className={styles['video-card-mobile-swiper']}
              modules={[Navigation, Pagination, Autoplay]}
              slidesPerView={1}
              loop={true}
              navigation={true}
              pagination={{ clickable: true }}
              onSlideChange={handleSlideChange}
              onTouchStart={() => handleTouchStart()}
              autoplay={!hasBeenSwiped ? {
                delay: 3000,
                disableOnInteraction: false
              } : false}
              onSwiper={(swiper: SwiperType) => {
                swiperRef.current = swiper;
              }}
            >
              {images.map((img, idx) => (
                <SwiperSlide key={idx} className={styles['video-card-mobile-slide']}>
                  <div className={styles['video-card-mobile-slide-inner']}>
                    <ThumbnailImage
                      className={styles['video-card-mobile-thumbnail']}
                      src={normalizeVideoImagePath(img.image_path)}
                      alt={img.description || '動画画像'}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {hasMultipleImages && !hasBeenSwiped && (
              <div
                className={styles['video-card-mobile-swipe-indicator']}
                data-testid="video-card-mobile__swipe-indicator"
              >
                <span className={styles['video-card-mobile-swipe-icon']}>← スワイプ →</span>
              </div>
            )}
          </>
        ) : (
          video.thumbnail ? (
            <ThumbnailImage
              className={`${styles['video-card-mobile-thumbnail']} ${styles.cover}`}
              src={getThumbnailUrl(video.thumbnail) || getDefaultThumbnailUrl()}
              alt={video.name || ''}
            />
          ) : isThumbnailPending(video) ? (
            <div className={styles['video-card-mobile-spinner-container']}>
              <div className={styles['video-card-mobile-spinner']}></div>
            </div>
          ) : (
            <ThumbnailImage
              className={`${styles['video-card-mobile-thumbnail']} ${styles.cover}`}
              src={getDefaultThumbnailUrl()}
              alt={video.name || ''}
            />
          )
        )}

        {/* 再生時間 */}
        <div className={styles['video-card-mobile-duration']}>
          {formatDuration(video.duration)}
        </div>

        {/* 公開状態バッジ */}
        {video.is_processing ? (
          <span
            className={styles['video-card-mobile-visibility-badge']}
            data-visibility="processing"
          >
            変換中
          </span>
        ) : (visibility === 'hospital_only' || visibility === 'public') && (
          <span
            className={styles['video-card-mobile-visibility-badge']}
            data-visibility={visibility}
          >
            {VISIBILITY_BADGE_LABELS[visibility]}
          </span>
        )}

        {/* 資料バッジ */}
        {!!video.materials?.length && (
          <span className={styles['video-card-mobile-materials-badge']} data-testid="video-card-mobile__materials-badge">
            資料 {video.materials.length}
          </span>
        )}
      </div>

      {/* 情報部分 */}
      <div className={styles['video-card-mobile-info-wrapper']}>
        {/* タイトル行 + ケバブメニュー */}
        <div className={styles['video-card-mobile-title-row']}>
          <h3 className={styles['video-card-mobile-title']} title={video.title || video.name || ''}>
            {video.title || video.name || ''}
          </h3>
          <KebabMenu
            isOpen={kebabMenu.isMenuOpen}
            position={kebabMenu.menuPosition}
            onClose={kebabMenu.closeMenu}
            isFavorite={isFavorite}
            isOwner={isOwner}
            isPrivate={isPrivate}
            currentVisibility={visibility}
            isProcessing={isVideoProcessing}
            hideVisibilityToggle={isVideoProcessing}
            hideFavorite={isVideoProcessing}
            hideEdit={false}
            isExternal={isExternal}
            hasSpecialty={hasSpecialty}
            onFavoriteClick={kebabMenu.handleFavoriteMenuClick}
            onVisibilityClick={kebabMenu.handleVisibilityMenuClick}
            onEditClick={kebabMenu.handleEditMenuClick}
            onDownloadClick={isVideoProcessing ? undefined : kebabMenu.handleDownloadMenuClick}
            onDeleteClick={kebabMenu.handleDeleteMenuClick}
            menuRef={kebabMenu.menuRef}
            buttonRef={kebabMenu.buttonRef}
            onToggle={kebabMenu.toggleMenu}
            testIdPrefix="video-card-mobile"
          />
        </div>

        {/* アイコン + メタ情報 */}
        <div className={styles['video-card-mobile-meta-row']}>
          <img
            className={styles['video-card-mobile-uploader-icon']}
            src={uploaderIconUrl}
            alt="Uploader Icon"
            data-testid="video-card-mobile__uploader-icon"
          />
          <div className={styles['video-card-mobile-meta-text']}>
            <span
              className={styles['video-card-mobile-channel-name']}
              title={channelLabel}
              data-testid="video-card-mobile__channel-name"
            >
              {channelLabel}
            </span>
            <div className={styles['video-card-mobile-meta']}>
              <span className={styles['video-card-mobile-date']}>
                {`投稿日:${formatDate(video.created_at)}`}
              </span>
              {/* TODO: 手術日入力UIが実装されたら ?? new Date().toISOString() を削除すること */}
              <>
                <span className={styles['video-card-mobile-separator']}>•</span>
                <span className={styles['video-card-mobile-date']}>
                  {`手術日:${formatDate(video.surgery_date ?? new Date().toISOString())}`}
                </span>
              </>
            </div>
          </div>
        </div>
      </div>

      {/* カスタムダイアログ */}
      <CustomDialog
        isOpen={dialogOpen}
        title={dialogConfig.title}
        message={dialogConfig.message}
        confirmText={dialogConfig.confirmText}
        cancelText="キャンセル"
        onConfirm={() => {
          setDialogOpen(false);
          dialogConfig.onConfirm();
        }}
        onCancel={() => setDialogOpen(false)}
        type={dialogConfig.type}
      />

    </div>
  );
};

export default memo(VideoCardMobile);
